import get from 'lodash.get';
import { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import PropTypes from 'prop-types';
import styles from '../../styles/main.scss';

// components
import AddressForm from './AddressForm';
import Select from '../inputs/Select';
import Submit from '../inputs/Submit';
import { Radio } from 'dibs-elements/exports/Radio';

// doesn't need to be intl'ed since
// they are not visible on the client
const OPTIONS = {
    OPTION_ADD: 'ADD',
    OPTION_EXIST: 'EXIST',
};

class AddressSelector extends Component {
    constructor(props) {
        super(props);

        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.state = {
            ...this.getAddressState(props),
            isSaving: false,
        };
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { submitTriggered: prevSubmitTriggered } = this.props;
        const { submitTriggered, hideSubmit } = nextProps;
        const { selection } = this.state;

        if (
            selection === OPTIONS.OPTION_EXIST &&
            hideSubmit &&
            !prevSubmitTriggered &&
            submitTriggered
        ) {
            this.onSubmit();
        }

        if (this.props.address !== nextProps.address) {
            this.setState(this.getAddressState(nextProps));
        }
    }
    findAddress(addressId) {
        const { user } = this.props;
        const addressList = get(user, 'addressBook.addressList') || [];
        return addressList.find(a => a.serviceId === addressId);
    }
    getAddressState(props) {
        let addressId = get(props, 'user.firstValidShippingAddress.serviceId');
        let selection = addressId ? OPTIONS.OPTION_EXIST : OPTIONS.OPTION_ADD;

        if (props.address && props.address.serviceId && this.findAddress(props.address.serviceId)) {
            selection = OPTIONS.OPTION_EXIST;
            addressId = props.address.serviceId;
        } else if (props.address && !props.address.serviceId) {
            selection = OPTIONS.OPTION_ADD;
        }

        return {
            selection,
            addressId,
        };
    }
    onChange(payload) {
        const value = get(payload, 'value') || payload;
        const isAdding = value === OPTIONS.OPTION_ADD;
        const addressId = isAdding ? this.state.addressId : value;
        const selection = isAdding ? OPTIONS.OPTION_ADD : OPTIONS.OPTION_EXIST;
        this.setState({ selection, addressId });
    }
    onSubmit(payload) {
        let { address, isValid } = payload || {};
        const { selection, addressId } = this.state;
        const { onSubmit, hideSubmit } = this.props;

        if (selection === OPTIONS.OPTION_EXIST) {
            address = this.findAddress(addressId);
            isValid = !!address;
        }

        if (isValid && !hideSubmit) {
            this.setState({ isSaving: true });
        }

        onSubmit({ address, isValid });
    }
    render() {
        const { selection, addressId, isSaving } = this.state;
        const {
            viewer,
            user,
            hideSubmit,
            submitTriggered,
            disableForm,
            fields,
            address,
            validations,
            disabled,
            copy,
            children,
        } = this.props;
        const isAdding = selection === OPTIONS.OPTION_ADD;
        const addresses = (get(user, 'addressBook.addressList') || []).filter(a =>
            get(a, 'validation.buyerShipping.valid')
        );
        const addressOptions = addresses.map(addressOption => ({
            value: addressOption.serviceId,
            label: [addressOption.displayName]
                .concat(addressOption.formattedAddress, [addressOption.displayCountry])
                .join(', '),
        }));
        const showAddresses = !!addressOptions.length;
        const showAddressForm = isAdding || !addresses.length;

        return (
            <div data-tn="dibs-address-form-address-selector">
                {showAddresses && (
                    <div className={styles.subContentWrapper}>
                        <Radio
                            name="addressAction"
                            wrapperClass={styles.radioWrapper}
                            label={copy.addFromSavedAddresses}
                            value={addressId}
                            checked={!isAdding}
                            disabled={isSaving || disableForm}
                            onChange={this.onChange}
                            dataTn="add-saved-address"
                        />
                        {!showAddressForm && (
                            <Select
                                wrapperClasses={styles.subContent}
                                name="address"
                                value={addressId}
                                options={addressOptions}
                                disabled={isSaving || disableForm}
                                onChange={this.onChange}
                                dataTn="saved-addresses"
                            />
                        )}
                    </div>
                )}
                <div className={styles.subContentWrapper}>
                    {showAddresses && (
                        <Radio
                            name="addressAction"
                            wrapperClass={styles.radioWrapper}
                            label={copy.addNewAddress}
                            value={OPTIONS.OPTION_ADD}
                            checked={isAdding}
                            disabled={isSaving || disableForm}
                            onChange={this.onChange}
                            dataTn="new-address"
                        />
                    )}
                    {showAddressForm && (
                        <AddressForm
                            viewer={viewer}
                            formClass={showAddresses ? styles.subContent : styles.singleFormWrapper}
                            submitLabel={copy.send}
                            onSubmit={this.onSubmit}
                            submitTriggered={submitTriggered}
                            hideSubmit={hideSubmit}
                            fields={fields}
                            address={address}
                            validations={validations}
                            disabled={disabled}
                            disableForm={disableForm}
                            children={children}
                        />
                    )}
                </div>
                {!showAddressForm && <div className={styles.subContentWrapper}>{children}</div>}
                {!showAddressForm && !hideSubmit && (
                    <Submit
                        label={copy.send}
                        isSaving={isSaving || disableForm}
                        onClick={this.onSubmit}
                    />
                )}
            </div>
        );
    }
}

AddressSelector.propTypes = {
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    copy: PropTypes.shape({
        addNewAddress: PropTypes.node,
        addFromSavedAddresses: PropTypes.node,
        addNewAddresses: PropTypes.node,
        send: PropTypes.node,
    }),
    submitTriggered: PropTypes.bool,
    hideSubmit: PropTypes.bool,
    disabled: PropTypes.array,
    disableForm: PropTypes.bool,
    fields: PropTypes.array,
    validations: PropTypes.object,
    address: PropTypes.object,
    children: PropTypes.node,
};

AddressSelector.defaultProps = {
    copy: {
        addFromSavedAddresses: (
            <FormattedMessage
                id="dc.AddressForm.AddressSelector.addFromSavedAddresses"
                defaultMessage="Add from saved addresses"
            />
        ),
        addNewAddress: (
            <FormattedMessage
                id="dc.AddressForm.AddressSelector.addNewAddress"
                defaultMessage="Add new address"
            />
        ),
        send: (
            <FormattedMessage
                id="dc.AddressForm.AddressSelector.send"
                defaultMessage="Save & Continue"
            />
        ),
    },
    disableForm: false,
    disabled: [],
};

export default createFragmentContainer(AddressSelector, {
    viewer: graphql`
        fragment AddressSelector_viewer on Viewer {
            ...AddressForm_viewer
        }
    `,
    user: graphql`
        fragment AddressSelector_user on User {
            serviceId
            firstValidShippingAddress {
                serviceId
            }
            addressBook {
                addressList {
                    sourceId
                    serviceId
                    displayName
                    formattedAddress
                    displayCountry
                    firstName
                    lastName
                    line1
                    line2
                    country
                    city
                    state
                    zipCode
                    phoneNumber
                    validation {
                        buyerShipping {
                            valid
                        }
                    }
                }
            }
        }
    `,
});
